/**
 * SettingsHome — ekran "Ustawienia" (UI FREEZE v1.0, 1:1 z makietą).
 *
 * Układ: 4 karty statusu → taby (Profil aktywny inline; pozostałe nawigują do istniejących
 * pod-stron) → treść Profil (Dane konta / Typ działalności / Kontakt) + prawy rail "APIS opiekun".
 * Dane realne (user + hodowla); pola tylko do podglądu, edycja przez istniejące strony.
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  User as UserIcon, Crown, ShieldCheck, Dog, Building2, Wrench,
  Phone, MessageCircle, Facebook, Instagram, MapPin, Mail,
  Sparkles, CheckCircle2, Circle, ArrowRight, ArrowUpRight, ExternalLink,
  Pencil, CreditCard, Loader2,
} from 'lucide-react';
import StudioShell from '@/components/studio/StudioShell';
import { useAuth } from '@/context/AuthContext';
import { useNewLeadsCount } from '@/hooks/useNewLeadsCount';
import { API } from '@/utils/api';

const PROFILE_TYPES = [
  { key: 'hodowla', label: 'Hodowca', desc: 'Psy i mioty', Icon: Dog },
  { key: 'firma', label: 'Firma', desc: 'Usługi i produkty', Icon: Building2 },
  { key: 'uslugi', label: 'Usługi', desc: 'Groomer, behawiorysta…', Icon: Wrench },
];

function computeCompleteness(kennel, dogsCount) {
  if (!kennel) return 0;
  const checks = [
    !!kennel.name,
    (kennel.description || '').length > 40,
    !!(kennel.cover_image || kennel.cover_photo || kennel.logo),
    !!(kennel.gallery && kennel.gallery.length),
    !!(kennel.city),
    !!(kennel.phone || kennel.whatsapp),
    !!(kennel.facebook || kennel.instagram || kennel.website),
    dogsCount > 0,
  ];
  return Math.round((checks.filter(Boolean).length / checks.length) * 100);
}

function CircularProgress({ value, size = 44 }) {
  const r = (size - 6) / 2;
  const c = 2 * Math.PI * r;
  return (
    <svg width={size} height={size} className="-rotate-90 flex-shrink-0">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#EEF0F2" strokeWidth="5" />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#10B981" strokeWidth="5"
        strokeDasharray={`${(value / 100) * c} ${c}`} strokeLinecap="round" className="transition-all duration-700" />
    </svg>
  );
}

function StatusCard({ Icon, iconColor, iconBg, label, value, cta, onCta, progress, testid }) {
  return (
    <div className="apisai-soft-card p-5" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: iconBg }}>
          <Icon className="w-5 h-5" style={{ color: iconColor }} strokeWidth={2} />
        </div>
        {typeof progress === 'number' && (
          <div className="relative">
            <CircularProgress value={progress} />
            <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-emerald-600">{progress}%</span>
          </div>
        )}
      </div>
      <p className="text-[var(--apisai-text-muted)] text-[11px] uppercase tracking-wide font-semibold mt-3">{label}</p>
      <p className="text-[var(--apisai-text-primary)] text-lg font-bold mt-0.5 truncate">{value}</p>
      {cta && (
        <button onClick={onCta} className="mt-1.5 inline-flex items-center gap-1 text-[12px] font-semibold text-[var(--apisai-primary)] hover:underline">
          {cta} <ArrowRight className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

function InfoRow({ Icon, label, value }) {
  const empty = !value;
  return (
    <div className="flex items-center gap-3 py-3 border-b border-[#F0F1F3] last:border-0">
      <Icon className="w-4 h-4 text-zinc-400 flex-shrink-0" />
      <span className="text-[13px] text-[var(--apisai-text-muted)] w-36 sm:w-44 flex-shrink-0">{label}</span>
      <span className={`text-[13px] font-medium flex-1 truncate ${empty ? 'text-zinc-300 italic' : 'text-[var(--apisai-text-primary)]'}`}>
        {value || 'Nie podano'}
      </span>
    </div>
  );
}

const SETTINGS_TABS = [
  { id: 'profil', label: 'Profil' },
  { id: 'plan', label: 'Plan', to: '/panel/ustawienia/platnosci' },
  { id: 'platnosci', label: 'Płatności', to: '/panel/ustawienia/platnosci' },
  { id: 'powiadomienia', label: 'Powiadomienia', to: '/panel/ustawienia/email' },
  { id: 'bezpieczenstwo', label: 'Bezpieczeństwo', to: '/panel/ustawienia/konto' },
  { id: 'integracje', label: 'Integracje', to: '/panel/ustawienia/webhooks' },
];

export default function SettingsHome() {
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const newLeads = useNewLeadsCount();
  const [loading, setLoading] = useState(true);
  const [kennel, setKennel] = useState(null);
  const [counts, setCounts] = useState({ dogs: 0, litters: 0 });

  useEffect(() => {
    (async () => {
      const h = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      try {
        const kRes = await axios.get(`${API}/kennels`, { headers: h }).catch(() => ({ data: [] }));
        const kennels = kRes.data || [];
        const myK = Array.isArray(kennels) ? kennels[0] : null;
        setKennel(myK);
        const kid = myK?.id;
        const [dRes, sRes, lRes] = await Promise.allSettled([
          kid ? axios.get(`${API}/kennels/${kid}/dogs`, { headers: h }) : Promise.resolve({ data: [] }),
          axios.get(`${API}/studs`, { headers: h }).catch(() => ({ data: [] })),
          axios.get(`${API}/litters`, { headers: h }).catch(() => ({ data: [] })),
        ]);
        const dogsN = dRes.status === 'fulfilled' ? (dRes.value.data || []).length : 0;
        const studsN = sRes.status === 'fulfilled' ? (sRes.value.data || []).length : 0;
        const littersN = lRes.status === 'fulfilled' ? (lRes.value.data || []).length : 0;
        setCounts({ dogs: dogsN + studsN, litters: littersN });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const profileType = user?.profile_type || 'hodowla';
  const profileLabel = PROFILE_TYPES.find((p) => p.key === profileType)?.label || 'Hodowca';
  const completeness = computeCompleteness(kennel, counts.dogs);
  const published = !!kennel?.slug;
  const inCatalog = published && kennel?.show_in_catalog !== false;
  const hasPets = counts.dogs + counts.litters > 0;
  const hasWhatsapp = !!(kennel?.whatsapp);

  const checklist = [
    { label: 'Konto aktywne', done: true },
    { label: 'Strona opublikowana', done: published },
    { label: 'Widoczna w katalogu', done: inCatalog },
    { label: 'Masz dodane psy lub mioty', done: hasPets },
    { label: 'Dodaj WhatsApp', done: hasWhatsapp },
  ];
  const firstTodo = checklist.find((c) => !c.done);

  if (loading) {
    return (
      <StudioShell mode="workspace" activeKey="settings" contextLabel="Ustawienia">
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 text-[var(--apisai-primary)] animate-spin" />
        </div>
      </StudioShell>
    );
  }

  return (
    <StudioShell mode="workspace" activeKey="settings" contextLabel="Ustawienia">
      <div className="max-w-[1600px] mx-auto px-3 sm:px-6 py-6 lg:py-8" data-testid="settings-home">
        {/* Header */}
        <header className="space-y-1 mb-6">
          <h1 className="text-[var(--apisai-text-primary)] text-3xl sm:text-4xl font-bold tracking-tight leading-tight">Ustawienia</h1>
          <p className="text-[var(--apisai-text-secondary)] text-sm sm:text-base">Zarządzaj swoim kontem, profilem i sposobem działania APISAI.</p>
        </header>

        <div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-6">
          {/* ─── Kolumna główna ─── */}
          <div className="space-y-6 min-w-0">
            {/* 4 karty statusu */}
            <section className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              <StatusCard testid="status-account" Icon={UserIcon} iconColor="#F47A3B" iconBg="rgba(244,122,59,0.1)" label="Konto" value={profileLabel} />
              <StatusCard testid="status-plan" Icon={Crown} iconColor="#8B5CF6" iconBg="rgba(139,92,246,0.1)" label="Plan" value="FREE" />
              <StatusCard testid="status-profile" Icon={UserIcon} iconColor="#10B981" iconBg="rgba(16,185,129,0.1)" label="Profil" value="Uzupełniony"
                progress={completeness} cta="Uzupełnij profil" onCta={() => navigate(kennel?.id ? `/wizard/hodowla-v2/${kennel.id}` : '/wizard/hodowla-v2')} />
              <StatusCard testid="status-status" Icon={ShieldCheck} iconColor="#10B981" iconBg="rgba(16,185,129,0.1)" label="Status" value={published ? 'Aktywne' : 'W przygotowaniu'}
                cta="Szczegóły" onCta={() => navigate('/panel/katalog')} />
            </section>

            {/* Taby */}
            <div className="flex gap-6 border-b border-[var(--polish-border,rgba(0,0,0,0.08))] overflow-x-auto" data-testid="settings-tabs">
              {SETTINGS_TABS.map((tab) => {
                const isActive = tab.id === 'profil';
                const disabled = !isActive; // narazie nieaktywne — aktywny tylko Profil
                return (
                  <button
                    key={tab.id}
                    onClick={() => { if (!disabled && tab.to) navigate(tab.to); }}
                    disabled={disabled}
                    className={`px-1 pb-3 text-[13px] font-semibold whitespace-nowrap transition-colors relative ${
                      isActive
                        ? 'text-[var(--apisai-primary)]'
                        : 'text-[var(--apisai-text-muted)] opacity-50 cursor-not-allowed'
                    }`}
                    data-testid={`settings-tab-${tab.id}`}
                  >
                    {tab.label}
                    {isActive && <span className="absolute bottom-0 left-0 right-0 h-[2px] bg-[var(--apisai-primary)] rounded-full" />}
                  </button>
                );
              })}
            </div>

            {/* Dane konta */}
            <div className="apisai-soft-card p-5 sm:p-6" data-testid="account-data">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-[var(--apisai-text-primary)] text-base font-bold">Dane konta</h3>
                <button onClick={() => navigate('/panel/ustawienia/konto')}
                  className="inline-flex items-center gap-1.5 px-3.5 h-9 rounded-xl text-[12px] font-semibold border border-[var(--apisai-primary)] text-[var(--apisai-primary)] hover:bg-[var(--apisai-primary)]/5 transition-colors"
                  data-testid="edit-account">
                  <Pencil className="w-3.5 h-3.5" /> Edytuj
                </button>
              </div>
              <div>
                <InfoRow Icon={UserIcon} label="Imię i nazwisko" value={user?.name} />
                <InfoRow Icon={Mail} label="Adres e-mail" value={user?.email} />
                <InfoRow Icon={Phone} label="Telefon" value={kennel?.phone} />
                <InfoRow Icon={MapPin} label="Miasto" value={kennel?.city} />
              </div>
            </div>

            {/* Typ działalności */}
            <div className="apisai-soft-card p-5 sm:p-6" data-testid="business-type">
              <h3 className="text-[var(--apisai-text-primary)] text-base font-bold mb-1">Typ działalności</h3>
              <p className="text-[var(--apisai-text-muted)] text-[12px] mb-4">Określa, jak wygląda Twój profil w katalogu</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {PROFILE_TYPES.map((pt) => {
                  const active = pt.key === profileType;
                  return (
                    <div key={pt.key}
                      className={`rounded-xl border p-4 flex items-center gap-3 ${active ? 'border-[var(--apisai-primary)] bg-[var(--apisai-primary)]/5' : 'border-zinc-200'}`}>
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ background: active ? 'rgba(244,122,59,0.12)' : '#F4F4F5', color: active ? '#F47A3B' : '#A1A1AA' }}>
                        <pt.Icon className="w-5 h-5" />
                      </div>
                      <div className="min-w-0">
                        <p className={`text-sm font-bold ${active ? 'text-[var(--apisai-text-primary)]' : 'text-zinc-500'}`}>{pt.label}</p>
                        <p className={`text-[11px] font-medium ${active ? 'text-emerald-600' : 'text-zinc-400'}`}>{active ? 'Aktywna ścieżka' : 'Nieaktywna'}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Kontakt dla klientów */}
            <div className="apisai-soft-card p-5 sm:p-6" data-testid="contact-data">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-[var(--apisai-text-primary)] text-base font-bold">Kontakt dla klientów</h3>
                <button onClick={() => navigate(kennel?.id ? `/wizard/hodowla-v2/${kennel.id}?krok=kontakt` : '/wizard/hodowla-v2')}
                  className="inline-flex items-center gap-1.5 px-3.5 h-9 rounded-xl text-[12px] font-semibold border border-[var(--apisai-primary)] text-[var(--apisai-primary)] hover:bg-[var(--apisai-primary)]/5 transition-colors"
                  data-testid="edit-contact">
                  <Pencil className="w-3.5 h-3.5" /> Edytuj kontakt
                </button>
              </div>
              <div>
                <InfoRow Icon={Phone} label="Telefon kontaktowy" value={kennel?.phone} />
                <InfoRow Icon={MessageCircle} label="WhatsApp" value={kennel?.whatsapp} />
                <InfoRow Icon={Facebook} label="Facebook" value={kennel?.facebook} />
                <InfoRow Icon={Instagram} label="Instagram" value={kennel?.instagram} />
              </div>
            </div>
          </div>

          {/* ─── Prawy rail: APIS opiekun ─── */}
          <aside className="space-y-4" data-testid="apis-rail">
            {/* Pomoc — na górze prawej kolumny */}
            <div className="apisai-soft-card p-5" data-testid="rail-help">
              <p className="text-[var(--apisai-text-primary)] text-sm font-bold">Potrzebujesz pomocy?</p>
              <p className="text-[var(--apisai-text-secondary)] text-[13px] mt-1">Asystent ApisAI odpowie na każde pytanie o platformę.</p>
              <button onClick={() => navigate('/panel/pomoc')} className="mt-3 inline-flex items-center gap-1 text-[13px] font-semibold text-[var(--apisai-primary)] hover:underline">
                Otwórz pomoc <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="apisai-soft-card p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg,#F97316,#FB923C)' }}>
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-[var(--apisai-text-primary)] text-[15px] font-bold leading-tight">APIS</p>
                  <p className="text-[var(--apisai-text-muted)] text-[12px]">Twój opiekun</p>
                </div>
              </div>

              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[13px] font-medium text-[var(--apisai-text-secondary)]">Profil uzupełniony</span>
                <span className="text-[14px] font-bold text-emerald-600">{completeness}%</span>
              </div>
              <div className="h-2 rounded-full bg-zinc-100 overflow-hidden mb-4">
                <div className="h-full rounded-full transition-all duration-700" style={{ width: `${completeness}%`, background: 'linear-gradient(90deg,#34D399,#10B981)' }} />
              </div>

              <ul className="space-y-2.5">
                {checklist.map((c) => (
                  <li key={c.label} className="flex items-center gap-2.5">
                    {c.done
                      ? <CheckCircle2 className="w-[18px] h-[18px] text-emerald-500 flex-shrink-0" />
                      : <Circle className="w-[18px] h-[18px] text-zinc-300 flex-shrink-0" />}
                    <span className={`text-[13px] ${c.done ? 'text-[var(--apisai-text-secondary)]' : 'text-[var(--apisai-text-primary)] font-semibold'}`}>{c.label}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Podpowiedź APIS */}
            <div className="rounded-2xl p-5" style={{ background: 'rgba(139,92,246,0.07)', border: '1px solid rgba(139,92,246,0.15)' }} data-testid="apis-nudge">
              <p className="text-[var(--apisai-text-primary)] text-[15px] font-bold">{firstTodo ? 'Jeszcze jeden krok 🎯' : 'Dobrze Ci idzie! 🎉'}</p>
              <p className="text-[var(--apisai-text-secondary)] text-[13px] mt-1 leading-relaxed">
                {firstTodo
                  ? `${firstTodo.label} — to zwiększy liczbę zapytań od klientów.`
                  : 'Twój profil jest kompletny. Publikuj posty, aby zdobywać jeszcze więcej klientów.'}
              </p>
              <button onClick={() => navigate(firstTodo ? (kennel?.id ? `/wizard/hodowla-v2/${kennel.id}` : '/wizard/hodowla-v2') : '/panel/promocja')}
                className="mt-3 inline-flex items-center gap-1.5 px-4 h-10 rounded-xl text-[13px] font-semibold text-white transition-all hover:brightness-95"
                style={{ background: '#6366F1' }}>
                {firstTodo ? 'Uzupełnij profil' : 'Opublikuj post'} <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Szybkie linki */}
            <div className="apisai-soft-card p-5">
              <p className="text-[var(--apisai-text-primary)] text-sm font-bold mb-3">Szybkie linki</p>
              <div className="space-y-1">
                {kennel?.slug && (
                  <a href={`/hodowla/${kennel.slug}`} target="_blank" rel="noopener noreferrer"
                    className="flex items-center justify-between py-2 text-[13px] text-[var(--apisai-text-secondary)] hover:text-[var(--apisai-primary)] transition-colors">
                    Zobacz profil w katalogu <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                )}
                <button onClick={() => navigate('/panel/moje')} className="w-full flex items-center justify-between py-2 text-[13px] text-[var(--apisai-text-secondary)] hover:text-[var(--apisai-primary)] transition-colors">
                  Twoje strony <ArrowUpRight className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => navigate('/panel/leady')} className="w-full flex items-center justify-between py-2 text-[13px] text-[var(--apisai-text-secondary)] hover:text-[var(--apisai-primary)] transition-colors">
                  <span className="inline-flex items-center gap-2">Zapytania
                    {newLeads > 0 && <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold text-white" style={{ background: 'var(--apisai-primary)' }}>{newLeads}</span>}
                  </span>
                  <ArrowUpRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

          </aside>
        </div>
      </div>
    </StudioShell>
  );
}
